import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { BRAND } from "../data/site";

const links = [
  { label: "Work", to: "/projects" },
  { label: "Services", to: "/#services" },
  { label: "Approach", to: "/#approach" },
  { label: "Contact", to: "/projects#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-obsidian/70 backdrop-blur-xl border-b border-border"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-[1500px] items-center justify-between px-6 md:px-12">
          <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2">
            <img src={BRAND.logo} alt="101 Contractor LLC" className="h-12 w-auto rounded-md" />
          </Link>

          <nav className="hidden items-center gap-10 md:flex">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="group relative text-[13px] uppercase tracking-[0.18em] text-sand transition-colors duration-300 hover:text-white"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-ember transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </nav>

          <a
            href={BRAND.phoneHref}
            data-testid="nav-call-btn"
            className="hidden items-center gap-2 border border-border px-5 py-2.5 text-[12px] uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-ember hover:text-ember md:flex"
          >
            <Phone className="h-3.5 w-3.5" />
            {BRAND.phone}
          </a>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((v) => !v)}
            className="text-white md:hidden"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 flex flex-col justify-center gap-6 bg-obsidian px-8 md:hidden"
          >
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                className="font-display text-4xl text-white"
              >
                {l.label}
              </Link>
            ))}
            <a href={BRAND.phoneHref} className="mt-6 text-ember tracking-[0.15em]">
              {BRAND.phone}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
